import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { DetectedFileKind, ErrorCode, assertNever, moneyMovementLabel, parseDetectedFileKind } from '@signalvote/shared';
import type {
  CompiledColumnMappingDto,
  CompiledColumnResolutionDto,
  DetectionResponseDto,
  DetectorTrainRequestDto,
  DetectorPreviewResponseDto,
} from '@signalvote/shared';
import {
  useDetectFile,
  useConfirmDetection,
  useTrainDetector,
  useDetectorPreview,
  fileToBase64,
  DetectorErrorWithCode,
} from '@/hooks/queries/useFileTypeDetector';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { ColumnMappingGrid, LockedPeriodSkipPanel, lockedIncludeHint } from '@/components/finance/import';
import type { ColumnTarget } from '@/components/finance/import';
import { DetectionConfirmationBanner } from './DetectionConfirmationBanner';
import { DetectionSummaryBanner } from './DetectionSummaryBanner';
import { KindPicker } from './KindPicker';
// Sibling component in the same folder: direct import keeps the dependency
// graph readable without the redundant barrel hop. The barrel export at
// `index.ts` is for OUTSIDE callers (e.g. OnboardingPage importing the
// shared copy constant).
import { AnedotGrossDepositsBanner } from './AnedotGrossDepositsBanner';
import { readyProfile } from '@/lib/complianceProfileRead';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Debounce window for the compiled mapping re-preview. A treasurer correcting
// several opaque columns fires a change per pick; without this, each one blanks
// the preview and re-uploads the whole file. One trailing round-trip after they
// pause reads as instant instead of a cascade of reloads.
const PREVIEW_DEBOUNCE_MS = 350;

type Done = { batchId: string; preservedForFutureKind?: string | null };

/**
 * Drop-or-click upload panel that wires the inbox detector to the
 * confirmation-banner / kind-picker UX. Stages:
 *
 *   * idle: drop zone, awaiting a file.
 *   * detected (High confidence): confirmation banner; user clicks Yes/No.
 *   * picker: kind picker, pre-selecting the detector's header-token guess
 *     (`guessedKind`). Shown on Low/None confidence, or when the user clicked
 *     "No, this is something else" on the banner.
 *   * previewing (ADR-015 D7): once a kind is resolved (banner-confirmed OR
 *     picker-picked) for a previewable adapter kind, run the dry-run /preview
 *     and show the detection summary + row grid as a pre-commit review. The
 *     actual commit now lives behind a SECOND explicit "Import these rows"
 *     action. Bank / ActBlue / "Not sure" kinds have no adapter preview and
 *     commit directly as before.
 *   * importing: the train/confirm commit call is in flight.
 *   * done: ImportBatch created — surface the next step (inbox link).
 *
 * On "No, this is something else" the previously-detected kind is hidden
 * from the picker (per acceptance criteria).
 */
export function FileTypeDetectorPanel({
  onImported,
  initialFile,
  onRemove,
  startInPicker,
}: {
  /**
   * Called after a successful train / confirm (non-bank), OR — for bank kinds —
   * as the deferred hand-off to the parent. The first arg is the created
   * ImportBatch id for non-bank commits, or NULL for bank kinds (bank files
   * defer batch creation to the bank account step, so no batch exists yet). The
   * second arg carries the detection result so callers (the multi-file front
   * door) can branch on kind / bank profile id without re-detecting. The third
   * arg flags a mixed-source import (the "Not sure / it's a mix" pick) so the
   * bank UI can drop the "this looks like a bank statement" framing — the rows
   * route through the same BankCsv path but may not belong to a real bank account.
   */
  onImported?: (
    importBatchId: string | null,
    detection?: DetectionResponseDto | null,
    meta?: { mixedSource?: boolean; skippedRowCount?: number },
  ) => void;
  /**
   * ADR-015 D11 — embedded mode. When the multi-file front door has already
   * selected a file for this row, hand it in here: the panel skips its own
   * drop zone and auto-detects on mount, so it acts as one instance of the
   * single-file pipeline (no fork). Omit it for the standalone single-file
   * drop-zone behavior.
   */
  initialFile?: File;
  /**
   * Embedded-mode "Cancel"/remove. When set, the picker/preview cancel actions
   * remove this file's row (the parent owns the file list) instead of resetting
   * to a blank drop zone that embedded mode never shows.
   */
  onRemove?: () => void;
  /**
   * Open the kind PICKER straight after auto-detect, regardless of confidence.
   * Used when the parent re-shows the detector for the SAME file after a "Choose
   * a different type" action from a later stage (e.g. the bank account step) —
   * we want the picker, not the high-confidence banner / an immediate bank
   * re-hand-off.
   */
  startInPicker?: boolean;
}) {
  const complianceProfile = readyProfile(useComplianceProfile());
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  // Commit-time duplicate (server hard-block) is rendered as a soft-amber
  // heads-up, NOT the red serverError surface — a re-upload is a normal
  // "already did this", not a failure (ADR-015 D9 / the emphasis ladder).
  const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);
  const [detection, setDetection] = useState<DetectionResponseDto | null>(null);
  const detectedKind = parseDetectedFileKind(detection?.kind) ?? DetectedFileKind.Unattributed;
  const guessedKind = parseDetectedFileKind(detection?.guessedKind);
  const [showPicker, setShowPicker] = useState(false);
  const [omittedKind, setOmittedKind] = useState<DetectedFileKind | null>(null);
  // The kind the treasurer resolved (banner-confirmed or picker-picked) plus
  // which commit mutation it commits with. Set once we leave the banner/picker;
  // drives the preview + commit stages.
  const [resolvedKind, setResolvedKind] = useState<DetectedFileKind | null>(null);
  // When the treasurer returns to the picker via "Choose a different type" (from
  // the preview stage), pre-select the kind they're leaving so the picker opens
  // on it (not the original header-token guess). Cleared on reset.
  const [changeTypeInitial, setChangeTypeInitial] = useState<DetectedFileKind | null>(null);
  const [commitMode, setCommitMode] = useState<'confirm' | 'train'>('confirm');
  const [preview, setPreview] = useState<DetectorPreviewResponseDto | null>(null);
  // The treasurer's manual column mapping for a compiled (Donations/Expenses)
  // file — the universal grid's payload. Null until the grid emits a change;
  // omitted from the request for adapter / bank kinds (fuzzy default untouched).
  // One-off: it rides the request for THIS import only (no profile persisted).
  const [compiledMapping, setCompiledMapping] = useState<CompiledColumnMappingDto | null>(null);
  // Locked-period rows (dated in an already-filed period) the treasurer chose to
  // force-include, tracked by their stable `rowIndex`. Empty = the default:
  // skip every locked row so a re-upload can't double-report. Reset with each
  // fresh preview so the skip-by-default baseline always holds.
  const [selectedLockedRows, setSelectedLockedRows] = useState<number[]>([]);
  const [done, setDone] = useState<Done | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The pending debounced mapping re-preview timer (cleared/rescheduled on each
  // column change; cancelled on every exit from the review stage and on unmount).
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic preview request id. A response whose id no longer matches the
  // latest is dropped (latest-wins) so a slow, superseded re-preview can't
  // clobber a newer one — matters once rapid mapping changes overlap on the wire.
  const previewSeqRef = useRef(0);
  // True while a mapping re-preview is pending (debounce wait) or in flight. Dims
  // the stale preview with an "updating" hint instead of blanking it, and holds
  // the Import button so a commit can't pair the new mapping with the old
  // preview's locked-row candidates.
  const [previewRefreshing, setPreviewRefreshing] = useState(false);

  // Drop any pending/in-flight mapping re-preview and clear the updating state.
  // Bumping the seq invalidates a response already on the wire.
  const cancelScheduledPreview = useCallback(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    previewSeqRef.current += 1;
    setPreviewRefreshing(false);
  }, []);

  // Clear a pending debounce timer on unmount so it can't fire post-teardown.
  useEffect(() => {
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, []);

  const { mutateAsync: detectAsync, isPending: detecting } = useDetectFile();
  const { mutateAsync: confirmAsync, isPending: confirming } = useConfirmDetection();
  const { mutateAsync: trainAsync, isPending: training } = useTrainDetector();
  const { mutateAsync: previewAsync, isPending: previewing } = useDetectorPreview();
  const busy = detecting || confirming || training || previewing;
  // The banner / picker / commit actions only ever render AFTER detection has
  // resolved, so they must NOT be gated on `detecting`. Under StrictMode the
  // detect mutation's observer can desync — the detection resolves but
  // `detecting` stays stuck `true` — which would otherwise permanently disable
  // (and mislabel "Importing…") every post-detection action. Gate those
  // surfaces on the commit/preview flags only; keep the full `busy` (including
  // `detecting`) for the Dropzone's "Inspecting file…" state.
  const actionBusy = confirming || training || previewing;

  const reset = useCallback(() => {
    setFile(null);
    setClientError(null);
    setServerError(null);
    setDuplicateMessage(null);
    setDetection(null);
    setShowPicker(false);
    setOmittedKind(null);
    setResolvedKind(null);
    setChangeTypeInitial(null);
    setPreview(null);
    setCompiledMapping(null);
    setSelectedLockedRows([]);
    setDone(null);
    cancelScheduledPreview();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [cancelScheduledPreview]);

  // Embedded-mode cancel removes this file's row; standalone cancel resets to
  // the empty drop zone.
  const dismiss = useCallback(() => {
    if (onRemove) onRemove();
    else reset();
  }, [onRemove, reset]);

  const handleFile = useCallback(async (f: File) => {
    setClientError(null);
    setServerError(null);
    setDuplicateMessage(null);
    setDone(null);
    if (f.size > MAX_FILE_SIZE) {
      setClientError('File too large. Maximum 10MB.');
      return;
    }
    setFile(f);
    try {
      const result = await detectAsync(f);
      setDetection(result);
      // Open the picker on a low-confidence guess OR when the parent re-shows the
      // detector to re-pick the kind for the SAME file (startInPicker) — in the
      // latter case we want the picker, not the high-confidence banner / an
      // immediate bank re-hand-off.
      if (result.confidence !== 'High' || startInPicker) {
        setShowPicker(true);
      }
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Detection failed.');
    }
  }, [detectAsync, startInPicker]);

  // ADR-015 D11 — embedded mode: auto-detect the handed-in file once on mount so
  // each multi-file row drives the existing single-file pipeline without its own
  // drop zone.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (initialFile && !autoStartedRef.current) {
      autoStartedRef.current = true;
      void handleFile(initialFile);
    }
  }, [initialFile, handleFile]);

  const handleServerError = useCallback((e: unknown) => {
    if (e instanceof DetectorErrorWithCode) {
      // Already-imported (server hard-block) → friendly amber heads-up, not the
      // red error surface. The message IS the server's campaign-local copy
      // ("You've already imported this file on M/d/yyyy…"), rendered verbatim.
      if (e.code === ErrorCode.DetectorDuplicateUpload) {
        setDuplicateMessage(e.message);
        return;
      }
      // ParserRejected → re-open picker omitting the previous answer.
      // (Hooked up here for future use when adapters can actually reject.)
      if (e.code === ErrorCode.DetectorParserRejected && detection) {
        setOmittedKind(detectedKind);
        setShowPicker(true);
      }
      setServerError(e.message);
      return;
    }
    setServerError(e instanceof Error ? e.message : 'Import failed.');
  }, [detection, detectedKind]);

  // The single commit tail — confirm (detector guess) or train (picker pick).
  const commit = useCallback(async (kind: DetectedFileKind, mode: 'confirm' | 'train', mixedSource = false) => {
    if (!file) return;
    setServerError(null);
    setDuplicateMessage(null);
    try {
      const fileBytesBase64 = await fileToBase64(file);
      // Force-includes for rows dated in an already-filed period. Ordered by the
      // preview's candidate list (not click order) and filtered to the current
      // candidate set, so the wire is deterministic and never carries a stale
      // rowIndex. Empty ⇒ omitted ⇒ the server skips every locked row (default).
      const orderedLockedIncludes = (preview?.summary.lockedPeriodCandidates ?? [])
        .filter((c) => selectedLockedRows.includes(c.rowIndex))
        .map((c) => c.rowIndex);
      const request: DetectorTrainRequestDto = {
        headers: detection?.headers ?? [],
        resolvedKind: kind,
        fileName: file.name,
        fileSha256: null,
        fileBytesBase64,
        // Bank kinds never reach commit (they defer to the parent), so this is
        // effectively always null here; kept for wire-shape completeness.
        bankSourceProfileId:
          mode === 'confirm' ? (detection?.bankSourceProfileId ?? null) : null,
        // The one-off manual column mapping rides the commit re-scan so the
        // SAME override the preview showed applies — no profile persisted.
        // Compiled (Donations/Expenses) only; absent for adapter / bank kinds.
        compiledMapping: isCompiledKind(kind) ? compiledMapping : null,
        includeLockedRowIndices: orderedLockedIncludes.length > 0 ? orderedLockedIncludes : null,
        // Bind the force-includes to the exact bytes the preview parsed (the
        // sha-guard). Harmless without includes; pass whenever a preview ran.
        previewFileSha256: preview?.previewFileSha256 || null,
      };
      const result =
        mode === 'confirm' ? await confirmAsync(request) : await trainAsync(request);
      setDone({
        batchId: result.importBatchId,
        preservedForFutureKind: result.preservedForFutureKind,
      });
      // Hand the CHOSEN (non-bank) kind + the created batch id to the parent.
      // Bank kinds never reach here — they defer to the parent via
      // handBankToParent (no batch). The original headers / sampleRows ride along.
      const resolvedDetection = detection ? { ...detection, kind } : detection;
      // Carry the count of rows dropped for landing in an already-filed period
      // so the parent row can surface a calm heads-up (bank kinds report their
      // own count from the deferred bank commit instead).
      onImported?.(result.importBatchId, resolvedDetection, {
        mixedSource,
        skippedRowCount: result.skippedRowCount,
      });
    } catch (e) {
      handleServerError(e);
    }
  }, [file, detection, compiledMapping, preview, selectedLockedRows, confirmAsync, trainAsync, onImported, handleServerError]);

  // ADR-015 D7: dry-run the resolved kind and move to the pre-commit review
  // stage. Fires once per resolved kind. Preview is reassurance, not a gate —
  // but a parse failure here means the commit would fail too, so surface it and
  // fall back to the picker rather than committing blind.
  //
  // `keepPrevious` distinguishes the two re-fire paths. The INITIAL preview
  // (false) blanks the prior summary while it loads — the review stage isn't up
  // yet, so there's nothing to keep. A mapping-change re-preview (true) keeps the
  // last good preview on screen (dimmed, "updating") instead of blanking on every
  // pick, and on failure stays in the review stage rather than ejecting to the
  // picker for what is usually a transient mis-map.
  const runPreview = useCallback(async (
    kind: DetectedFileKind,
    mode: 'confirm' | 'train',
    mapping?: CompiledColumnMappingDto | null,
    keepPrevious = false,
  ) => {
    if (!file) return;
    // Latest-wins: only the newest request applies its result / clears the
    // updating flag; a superseded response is dropped.
    const seq = ++previewSeqRef.current;
    setServerError(null);
    setCommitMode(mode);
    setResolvedKind(kind);
    if (keepPrevious) setPreviewRefreshing(true);
    else setPreview(null);
    try {
      const fileBytesBase64 = await fileToBase64(file);
      const request: DetectorTrainRequestDto = {
        headers: detection?.headers ?? [],
        resolvedKind: kind,
        fileName: file.name,
        fileBytesBase64,
        bankSourceProfileId:
          mode === 'confirm' ? (detection?.bankSourceProfileId ?? null) : null,
        // Re-preview with the treasurer's manual column mapping so the grid
        // shows the rows under their picks. Compiled kinds only.
        compiledMapping: isCompiledKind(kind) ? (mapping ?? null) : null,
        fileSha256: null,
        includeLockedRowIndices: null,
        previewFileSha256: null,
      };
      const result = await previewAsync(request);
      if (seq !== previewSeqRef.current) return; // superseded — drop
      setPreview(result);
      // Each fresh preview restores the skip-by-default baseline — a re-preview
      // (e.g. a compiled mapping change) recomputes which rows are locked, so a
      // carried-over selection could reference a row that's no longer a candidate.
      setSelectedLockedRows([]);
    } catch (e) {
      if (seq !== previewSeqRef.current) return; // superseded — drop
      if (keepPrevious) {
        // Keep the last good preview + the treasurer's picks; surface the error
        // so they can fix the mapping in place instead of restarting.
        handleServerError(e);
      } else {
        setResolvedKind(null);
        setPreview(null);
        setSelectedLockedRows([]);
        handleServerError(e);
      }
    } finally {
      if (seq === previewSeqRef.current) setPreviewRefreshing(false);
    }
  }, [file, detection, previewAsync, handleServerError]);

  // Toggle one locked row's inclusion (by its stable rowIndex).
  const toggleLockedRow = useCallback((rowIndex: number) => {
    setSelectedLockedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex],
    );
  }, []);

  // Include every locked row, or skip them all again when they're all included.
  const toggleAllLocked = useCallback(() => {
    const all = (preview?.summary.lockedPeriodCandidates ?? []).map((c) => c.rowIndex);
    setSelectedLockedRows((prev) => (all.every((i) => prev.includes(i)) ? [] : all));
  }, [preview]);

  // Bank files defer their ImportBatch to the bank account step (orphan-batch
  // fix), so the detector creates NOTHING for them — it hands the CHOSEN kind to
  // the parent, which owns the account picker + the deferred bank commit. The
  // parent must see kind = BankCsv (not the original Unattributed auto-detection)
  // so it routes into the bank flow; the original headers / sampleRows ride along
  // for the mapping panel. No batch id exists yet, so the first arg is null.
  const handBankToParent = useCallback((kind: DetectedFileKind, mixedSource: boolean) => {
    setServerError(null);
    setDuplicateMessage(null);
    const resolvedDetection = detection ? { ...detection, kind } : detection;
    onImported?.(null, resolvedDetection, { mixedSource });
  }, [detection, onImported]);

  // Previewable adapter kinds go through the review stage; ActBlue / "Not sure"
  // have no adapter preview and commit directly. Bank kinds defer entirely to the
  // parent (no preview, no commit here).
  const advance = useCallback(async (kind: DetectedFileKind, mode: 'confirm' | 'train', mixedSource = false) => {
    if (isBankKind(kind)) {
      handBankToParent(kind, mixedSource);
      return;
    }
    if (isPreviewableKind(kind)) {
      await runPreview(kind, mode);
    } else {
      setCommitMode(mode);
      await commit(kind, mode, mixedSource);
    }
  }, [runPreview, commit, handBankToParent]);

  // Return to the kind picker from the preview stage WITHOUT discarding the file
  // (FIX 3). Pre-select the kind being left (changeTypeInitial) and show ALL
  // options — the treasurer is deliberately re-choosing, so unlike "No, this is
  // something else" we do NOT omit anything.
  const handleChooseDifferentType = useCallback(() => {
    cancelScheduledPreview();
    setChangeTypeInitial(resolvedKind);
    setResolvedKind(null);
    setPreview(null);
    setCompiledMapping(null);
    setServerError(null);
    setDuplicateMessage(null);
    setOmittedKind(null);
    setShowPicker(true);
  }, [resolvedKind, cancelScheduledPreview]);

  const handleConfirmDetected = useCallback(async () => {
    if (!file || !detection) return;
    await advance(detectedKind, 'confirm');
  }, [file, detection, detectedKind, advance]);

  const handleRejectDetected = useCallback(() => {
    if (detection) {
      setOmittedKind(detectedKind);
    }
    setShowPicker(true);
  }, [detection, detectedKind]);

  const handlePick = useCallback(async (kind: DetectedFileKind, mixedSource = false) => {
    if (!file) return;
    await advance(kind, 'train', mixedSource);
  }, [file, advance]);

  // The second, explicit "Import these rows" action in the preview stage.
  const commitResolved = useCallback(async () => {
    if (!resolvedKind) return;
    // A pending re-preview would fire after the panel transitions to "done";
    // cancel it. (The button is disabled while refreshing, so this is a guard.)
    cancelScheduledPreview();
    await commit(resolvedKind, commitMode);
  }, [resolvedKind, commitMode, commit, cancelScheduledPreview]);

  // The targets the manual grid offers for the resolved compiled kind (Donor
  // name vs Payee; donation-only enrichment fields). Treasurer-first labels.
  const employerAddressRequired = complianceProfile?.employerAddressRequired ?? false;
  const compiledTargets = useMemo(
    () => (resolvedKind ? compiledTargetsForKind(resolvedKind, { employerAddressRequired }) : []),
    [resolvedKind, employerAddressRequired],
  );

  // Pre-fill the grid. Once the treasurer has touched the mapping, THEIR picks
  // are the source of truth — seed from our own compiledMapping state so every
  // pick (including the enrichment fields address/employer/occupation the
  // backend manual-scan echo does NOT round-trip) survives the
  // unmount/remount a live re-preview triggers. Before the first pick, seed from
  // the scanner's deterministic column resolutions (the fuzzy guesses). A future
  // LLM pre-fill would seed this SAME shape — the named, deferred seam (no LLM in
  // v1).
  const gridPrefill = useMemo(
    () =>
      compiledMapping
        ? prefillFromMapping(compiledMapping)
        : prefillFromResolutions(preview?.columnResolutions ?? []),
    [compiledMapping, preview?.columnResolutions],
  );

  // The treasurer changed a column in the manual grid: rebuild the compiled
  // mapping and store it IMMEDIATELY (the pick is instant and rides the commit),
  // then — once it carries the minimum date + amount — DEBOUNCE a re-preview so a
  // burst of column changes collapses into one round-trip instead of a reload per
  // pick. `previewRefreshing` flips on right away so the preview reads as
  // updating (dimmed) during the debounce wait, not frozen.
  const handleMappingChange = useCallback(
    (assignments: Record<string, number | null>) => {
      if (!resolvedKind) return;
      const mapping = assignmentsToCompiledMapping(assignments);
      setCompiledMapping(mapping);
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      if (mapping.amountColumn != null && mapping.dateColumn != null) {
        setPreviewRefreshing(true);
        previewDebounceRef.current = setTimeout(() => {
          previewDebounceRef.current = null;
          void runPreview(resolvedKind, commitMode, mapping, true);
        }, PREVIEW_DEBOUNCE_MS);
      } else {
        // The mapping lost a required column — nothing valid to preview; drop any
        // pending re-preview and clear the updating state.
        setPreviewRefreshing(false);
      }
    },
    [resolvedKind, commitMode, runPreview],
  );

  if (done) {
    return (
      <div className="rounded-md border bg-card p-4 space-y-3">
        <p className="text-sm">
          File imported. The inbox now has a new entry for{' '}
          <code className="bg-muted px-1 rounded">{file?.name}</code>.
        </p>
        {done.preservedForFutureKind && (
          <p className="text-xs text-muted-foreground">
            Saved for when {done.preservedForFutureKind} support ships — no
            rows produced yet.
          </p>
        )}
        {/* Embedded (multi-file) rows don't offer "upload another" — the front
            door owns adding more files; standalone single-file keeps it. */}
        {!initialFile && (
          <button
            type="button"
            onClick={reset}
            className="min-h-11 rounded-md border px-3 py-1.5 text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Upload another file
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Embedded mode hands in the file, so the drop zone is never shown
          (even after a reset, which embedded mode routes through onRemove). */}
      {!file && !initialFile && (
        <Dropzone
          onFile={handleFile}
          inputRef={fileInputRef}
          disabled={busy}
        />
      )}

      {clientError && (
        <p role="alert" className="text-sm text-red-600">
          {clientError}
        </p>
      )}
      {serverError && (
        <p role="alert" className="text-sm text-red-600">
          {serverError}
        </p>
      )}

      {/* ADR-015 D9 — already-imported heads-up. Soft-amber (never red); the
          server owns the hard block, so this informs rather than alarms. The
          message is the server's verbatim campaign-local copy. */}
      {duplicateMessage && (
        <div
          role="status"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-1"
        >
          <p>{duplicateMessage}</p>
        </div>
      )}

      {file && detection && isAnedotKind(detection.kind) && (
        <AnedotGrossDepositsBanner />
      )}

      {/* Banner + picker are hidden once a kind is resolved (we've moved on to
          the preview / commit stage). */}
      {file && detection && !showPicker && !resolvedKind
        && detection.confidence === 'High' && (
        <DetectionConfirmationBanner
          summary={detection.summary}
          detectedKind={detectedKind}
          onConfirm={handleConfirmDetected}
          onReject={handleRejectDetected}
          // Previewable kinds run a dry-run on confirm — the action is a look,
          // not a commit (the real commit is the later "Import these rows").
          // Direct-commit kinds (bank) keep the import-now phrasing.
          {...(isPreviewableKind(detectedKind)
            ? { prompt: 'Take a look first?', confirmLabel: 'See what we found', busyLabel: 'Inspecting…' }
            : {})}
          busy={actionBusy}
        />
      )}

      {file && showPicker && !resolvedKind && (
        <>
          {/* Guess-the-kind nudge: the detector returns a header-token
              `guessedKind` for every unrecognized file. The picker below
              pre-selects it; this one-liner names the guess and invites a
              correction (suggestion only — the treasurer still confirms by
              clicking Import; no inference / always-confirm). */}
          {guessedKind && (
            <p className="text-sm text-muted-foreground">
              This looks like {guessedKindNudgeLabel(guessedKind)} —
              we&apos;ve pre-selected it below. Change it if that&apos;s not
              right.
            </p>
          )}
          <KindPicker
            omitKind={omittedKind}
            initialSelected={changeTypeInitial ?? guessedKind ?? undefined}
            onPick={handlePick}
            onPickOther={(_name) => handlePick(DetectedFileKind.Unattributed)}
            // The "Not sure / it's a mix" pick routes through the same BankCsv
            // path but flags mixedSource so the bank step drops the bank-statement
            // framing (a mixed list may not belong to a real account).
            onPickMixed={() => handlePick(DetectedFileKind.BankCsv, true)}
            onCancel={dismiss}
            busy={actionBusy}
          />
        </>
      )}

      {/* ADR-015 D7 pre-commit review: detection summary + row grid, with the
          actual import behind a second explicit action. */}
      {file && resolvedKind && preview && (
        <div className="space-y-4">
          <DetectionSummaryBanner summary={preview.summary} />
          {/* Rows dated in an already-filed period: skipped by default, but the
              treasurer may force-include any/all. The commit carries the checked
              rowIndexes (above). Renders nothing when there are none. */}
          <LockedPeriodSkipPanel
            candidates={preview.summary.lockedPeriodCandidates ?? []}
            selected={selectedLockedRows}
            onToggle={toggleLockedRow}
            onToggleAll={toggleAllLocked}
          />
          {/* Universal manual column mapping for off-platform Donations/Expenses
              (no adapter). Pre-filled with the scanner's fuzzy guesses; the
              treasurer can correct an opaque-header file so it no longer
              dead-ends. The grid rides the commit for THIS import only — no
              profile is saved (one-off importable without saving). */}
          {isCompiledKind(resolvedKind) && (
            <section className="rounded-md border bg-card p-4 space-y-2">
              <div>
                <h4 className="text-sm font-medium">Map each column</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  We took our best guess below — change anything that&apos;s
                  off. Date and Amount are required.
                </p>
              </div>
              {/* The grid maps the file's REAL source columns — `sourceHeaders`,
                  NOT `headers`. On the compiled scanner path `headers` is the
                  fixed normalized "what we'll import" grid (Counterparty/Amount/
                  Date VALUES), so binding the grid to it would hide every opaque
                  source column past index 2 and emit indices that don't match the
                  column the backend binds. `sourceHeaders` carries the file's own
                  header row, the same array the column resolutions index into. */}
              <ColumnMappingGrid
                headers={preview.sourceHeaders}
                targets={compiledTargets}
                assignments={gridPrefill}
                onChange={handleMappingChange}
              />
            </section>
          )}
          {/* While a mapping change re-previews we DIM the last preview and show
              an "updating" hint rather than blanking it — the treasurer sees the
              rows shift under their new picks instead of a jarring reload. */}
          <div className="relative" aria-busy={previewRefreshing}>
            {previewRefreshing && (
              <p
                className="mb-1 text-xs text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                Updating preview…
              </p>
            )}
            <div
              className={
                previewRefreshing
                  ? 'pointer-events-none opacity-50 transition-opacity'
                  : 'transition-opacity'
              }
            >
              <PreviewRowGrid
                headers={preview.headers}
                rows={preview.sampleRows}
                movements={preview.sampleRowMovements}
              />
            </div>
          </div>
          {/* The preview is a dry run — reassure the treasurer nothing has been
              written yet. The real commit is the explicit "Import these rows". */}
          <p className="text-sm text-muted-foreground">
            Nothing has been imported yet — this is a preview. Review the rows
            above, then choose “Import these rows” to bring them in.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={commitResolved}
              // Hold the commit while a re-preview is pending/in-flight ONLY when
              // the (stale) preview carries locked-period candidates: importing
              // then would pair the new mapping with the OLD locked-row set, and a
              // changed date mapping can shift which rows fall in a filed period.
              // With no locked rows there's nothing stale to protect, so a fast
              // treasurer can still map-then-import without waiting on the refresh.
              disabled={
                actionBusy ||
                (previewRefreshing &&
                  (preview.summary.lockedPeriodCandidates ?? []).length > 0)
              }
              className="min-h-11 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {confirming || training
                ? 'Importing…'
                // The selection-reflecting hint ("(+N included)" / "(+0 held)")
                // appears only when there ARE locked rows to hold or include.
                : `Import these rows${
                    (preview.summary.lockedPeriodCandidates ?? []).length > 0
                      ? lockedIncludeHint(selectedLockedRows.length)
                      : ''
                  }`}
            </button>
            {/* FIX 3: re-pick the kind without discarding the file — returns to
                the picker, pre-selecting the kind being left. */}
            <button
              type="button"
              onClick={handleChooseDifferentType}
              disabled={actionBusy}
              className="min-h-11 rounded-md border px-4 py-1.5 text-sm hover:bg-muted disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Choose a different type
            </button>
            <button
              type="button"
              onClick={dismiss}
              disabled={actionBusy}
              className="min-h-11 rounded-md border px-4 py-1.5 text-sm hover:bg-muted disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {file && !detection && detecting && (
        <p className="text-sm text-muted-foreground">Inspecting file…</p>
      )}

      {file && resolvedKind && !preview && previewing && (
        <p className="text-sm text-muted-foreground">Inspecting file…</p>
      )}
    </div>
  );
}

/**
 * Which detected kinds have an adapter dry-run preview (ADR-015 D7). Mirrors
 * the backend's `ProcessorImportService.HandlesSource` set — bank kinds use
 * their own mapping preview, ActBlue is deferred (no adapter), and "Not sure"
 * has no parser, so `/preview` 400s (ParserRejected) for all of them. Gate them
 * out here so they commit directly as before instead of bouncing off a 400.
 */
function isPreviewableKind(kind: DetectedFileKind): boolean {
  switch (kind) {
    case DetectedFileKind.NumeroContributions:
    case DetectedFileKind.NumeroPayouts:
    case DetectedFileKind.WinRedContributions:
    case DetectedFileKind.WinRedPayouts:
    case DetectedFileKind.AnedotContributions:
    case DetectedFileKind.AnedotPayouts:
    case DetectedFileKind.StripeCombined:
    // ADR-015 Tier 2 — Donations/Expenses preview through the generic content
    // scanner's /preview branch (the scanner's per-row suggestions + summary).
    case DetectedFileKind.Donations:
    case DetectedFileKind.Expenses:
      return true;
    case DetectedFileKind.BankCsv:
    case DetectedFileKind.BankXlsx:
    case DetectedFileKind.BankOfx:
    case DetectedFileKind.ActBlueContributions:
    case DetectedFileKind.Unattributed:
      return false;
    // Exhaustive: a new DetectedFileKind must declare its preview eligibility
    // here (a silent default would 400 it off the scanner / mis-route a file).
    default:
      return assertNever(kind);
  }
}

/**
 * Compiled = the off-platform Donations/Expenses kinds the generic content
 * scanner serves (and the only kinds the universal manual-mapping grid applies
 * to). Adapter / bank / ActBlue / "Not sure" never carry a compiled mapping.
 */
function isCompiledKind(kind: DetectedFileKind): boolean {
  return kind === DetectedFileKind.Donations || kind === DetectedFileKind.Expenses;
}

/**
 * The three bank-shaped kinds (CSV / XLSX / OFX). These defer their ImportBatch
 * creation to the bank account step, so the detector hands them straight to the
 * parent — it never previews or commits them.
 */
function isBankKind(kind: DetectedFileKind): boolean {
  return kind === DetectedFileKind.BankCsv
    || kind === DetectedFileKind.BankXlsx
    || kind === DetectedFileKind.BankOfx;
}

/**
 * Treasurer-first phrase for the guess-the-kind nudge ("This looks like …").
 * The detector only ever guesses one of three header-token kinds for an
 * unrecognized file (bank/list, donations, expenses); the wire's PascalCase
 * `DetectedFileKind` is translated to plain words here at the render boundary —
 * no engineer enum names reach the treasurer. The fallback covers any future
 * kind without a wrong-sounding guess.
 */
function guessedKindNudgeLabel(kind: DetectedFileKind): string {
  switch (kind) {
    case DetectedFileKind.BankCsv:
      return 'a bank statement or a list of transactions';
    case DetectedFileKind.Donations:
      return 'an off-platform donations sheet';
    case DetectedFileKind.Expenses:
      return 'an expenses sheet';
    case DetectedFileKind.BankXlsx:
    case DetectedFileKind.BankOfx:
    case DetectedFileKind.NumeroContributions:
    case DetectedFileKind.NumeroPayouts:
    case DetectedFileKind.WinRedContributions:
    case DetectedFileKind.WinRedPayouts:
    case DetectedFileKind.ActBlueContributions:
    case DetectedFileKind.AnedotContributions:
    case DetectedFileKind.AnedotPayouts:
    case DetectedFileKind.StripeCombined:
    case DetectedFileKind.Unattributed:
    default:
      return 'a file we can bring in';
  }
}

// Grid target keys are the CompiledColumnMappingDto keys minus the `Column`
// suffix, so the grid's target→index record maps straight onto the dto.
const DONATION_TARGETS: ColumnTarget[] = [
  { value: 'date', label: 'Date' },
  { value: 'amount', label: 'Amount' },
  { value: 'name', label: 'Donor name' },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'ZIP' },
  { value: 'employer', label: 'Employer' },
  { value: 'occupation', label: 'Occupation' },
];

const EMPLOYER_ADDRESS_TARGETS: ColumnTarget[] = [
  { value: 'employerStreet', label: 'Employer street' },
  { value: 'employerCity', label: 'Employer city' },
  { value: 'employerState', label: 'Employer state' },
  { value: 'employerZip', label: 'Employer ZIP' },
];

const EXPENSE_TARGETS: ColumnTarget[] = [
  { value: 'date', label: 'Date' },
  { value: 'amount', label: 'Amount' },
  { value: 'name', label: 'Payee' },
  { value: 'purpose', label: 'Purpose' },
  // Payee address flows onto the created FinanceContact via the same
  // enrich-not-overwrite path donations use. AL's per-payee missing-address
  // filing guard needs it, so let the treasurer map these on import.
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'ZIP' },
];

export function compiledTargetsForKind(
  kind: DetectedFileKind,
  { employerAddressRequired }: { employerAddressRequired: boolean },
): ColumnTarget[] {
  if (kind === DetectedFileKind.Expenses) return EXPENSE_TARGETS;
  return employerAddressRequired ? [...DONATION_TARGETS, ...EMPLOYER_ADDRESS_TARGETS] : DONATION_TARGETS;
}

/**
 * Seed the grid's target→index pre-fill from the scanner's column resolutions.
 * The scanner's engineer-facing `concept` maps to a grid target key:
 * Counterparty → name (donor / payee), Description → purpose (expenses),
 * Amount → amount, Date → date. Unrecognized concepts are ignored (the grid
 * just shows them as skipped).
 */
function prefillFromResolutions(
  resolutions: CompiledColumnResolutionDto[],
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const r of resolutions) {
    const target = conceptToTarget(r.concept);
    if (target && out[target] == null) out[target] = r.columnIndex;
  }
  return out;
}

/**
 * Seed the grid's target→index pre-fill from the treasurer's OWN compiled
 * mapping (the source of truth once they've touched the grid). The dto keys are
 * the grid target keys plus a `Column` suffix, so this is the exact inverse of
 * {@link assignmentsToCompiledMapping}. Unlike {@link prefillFromResolutions},
 * this carries the enrichment fields (address/employer/occupation) the backend
 * manual-scan echo never round-trips — so a re-preview can't blank them.
 */
function prefillFromMapping(
  mapping: CompiledColumnMappingDto,
): Record<string, number | null> {
  return {
    date: mapping.dateColumn ?? null,
    amount: mapping.amountColumn ?? null,
    name: mapping.nameColumn ?? null,
    firstName: mapping.firstNameColumn ?? null,
    lastName: mapping.lastNameColumn ?? null,
    address: mapping.addressColumn ?? null,
    city: mapping.cityColumn ?? null,
    state: mapping.stateColumn ?? null,
    zip: mapping.zipColumn ?? null,
    employer: mapping.employerColumn ?? null,
    occupation: mapping.occupationColumn ?? null,
    employerStreet: mapping.employerStreetColumn ?? null,
    employerCity: mapping.employerCityColumn ?? null,
    employerState: mapping.employerStateColumn ?? null,
    employerZip: mapping.employerZipColumn ?? null,
    purpose: mapping.purposeColumn ?? null,
  };
}

function conceptToTarget(concept: string): string | null {
  switch (concept) {
    case 'Amount':
      return 'amount';
    case 'Date':
      return 'date';
    case 'Counterparty':
      return 'name';
    case 'First name':
      return 'firstName';
    case 'Last name':
      return 'lastName';
    case 'Description':
      return 'purpose';
    // The donation enrichment concepts the scanner echoes back on a manual scan
    // (the backend AddManualResolution path). Kept in sync with the grid targets
    // so a first-preview prefill from the resolutions is faithful for every
    // mapped concept, not just the core ones.
    case 'Employer':
      return 'employer';
    case 'Occupation':
      return 'occupation';
    case 'Address':
      return 'address';
    case 'City':
      return 'city';
    case 'State':
      return 'state';
    case 'Zip':
      return 'zip';
    case 'Employer street':
      return 'employerStreet';
    case 'Employer city':
      return 'employerCity';
    case 'Employer state':
      return 'employerState';
    case 'Employer ZIP':
      return 'employerZip';
    default:
      return null;
  }
}

/**
 * Map the grid's target→index record onto a {@link CompiledColumnMappingDto}.
 * The grid target keys are the dto keys minus the `Column` suffix.
 */
function assignmentsToCompiledMapping(
  assignments: Record<string, number | null>,
): CompiledColumnMappingDto {
  const at = (target: string) => (assignments[target] != null ? assignments[target]! : null);
  return {
    dateColumn: at('date'),
    amountColumn: at('amount'),
    nameColumn: at('name'),
    firstNameColumn: at('firstName'),
    lastNameColumn: at('lastName'),
    addressColumn: at('address'),
    cityColumn: at('city'),
    stateColumn: at('state'),
    zipColumn: at('zip'),
    employerColumn: at('employer'),
    occupationColumn: at('occupation'),
    employerStreetColumn: at('employerStreet'),
    employerCityColumn: at('employerCity'),
    employerStateColumn: at('employerState'),
    employerZipColumn: at('employerZip'),
    purposeColumn: at('purpose'),
    statusColumn: null,
  };
}

/**
 * Flat sampled-row grid for the preview (ADR-015 D7). Rows are plain
 * `string[][]` — no per-row discriminated union in this slice. No `truncate`
 * on headers (it clips at font-scale 1.3); the grid scrolls horizontally and
 * cells wrap instead.
 *
 * ADR-015 D8: the generic content scanner path supplies `movements` — a
 * per-row suggested money-movement PARALLEL to `rows`. When present, an
 * additive "Suggested classification" column renders the treasurer-first label
 * (translated from the PascalCase wire tag at the render boundary). The adapter
 * path passes none, so the column is absent there.
 */
function PreviewRowGrid({
  headers,
  rows,
  movements,
}: {
  headers: string[];
  rows: string[][];
  movements?: string[];
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No sample rows to show — the counts above cover the whole file.
      </p>
    );
  }
  const showMovements = !!movements && movements.length > 0;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-2 py-1 text-left font-medium">
                {h || `Column ${i + 1}`}
              </th>
            ))}
            {showMovements && (
              <th className="px-2 py-1 text-left font-medium">
                Suggested classification
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="border-b last:border-b-0">
              {row.map((cell, c) => (
                <td key={c} className="px-2 py-1 align-top">
                  {cell}
                </td>
              ))}
              {showMovements && (
                <td className="px-2 py-1 align-top text-muted-foreground">
                  {/* `moneyMovementLabel('')` returns '' (passthrough), so a row
                      without a parallel movement renders blank — no unsafe index. */}
                  {moneyMovementLabel(movements?.[r] ?? '')}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Returns true when the detected kind is one of Anedot&rsquo;s canonical kinds.
 * Used to gate the Gross Deposits educational banner. Kept here rather than on
 * the enum so the kind comparison is local to the only call site.
 */
function isAnedotKind(kind: DetectedFileKind | string): boolean {
  return kind === DetectedFileKind.AnedotContributions
    || kind === DetectedFileKind.AnedotPayouts;
}

function Dropzone({
  onFile,
  inputRef,
  disabled,
}: {
  onFile: (f: File) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <label
      className={
        'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 cursor-pointer text-sm '
        + (isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30')
      }
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f && !disabled) onFile(f);
      }}
    >
      <Upload className="w-6 h-6 text-muted-foreground" aria-hidden />
      <span>Drop a bank or processor file here, or click to browse.</span>
      <span className="text-xs text-muted-foreground">
        CSV / Excel / OFX up to 10MB.
      </span>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".csv,.xlsx,.xls,.ofx,.qfx"
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </label>
  );
}
